import React, { useLayoutEffect, useMemo, useRef } from 'react';
import gsap from 'gsap';
import { Draggable } from 'gsap/Draggable';

gsap.registerPlugin(Draggable);

export default function DraggableMarquee({
  items = [],
  renderItem,
  speed = 1,
  repeatCount = 3,
  className = '',
  pauseOnHover = true,
  throwMultiplier = 2.8,
  throwFriction = 0.975,
  maxThrowVelocity = 60,
  label = 'Partner marquee. Drag or use the left and right arrow keys.',
  cursorLabel = 'Drag',
}) {
  const rootRef = useRef(null);
  const trackRef = useRef(null);

  const duplicated = useMemo(() => Array.from({ length: repeatCount }).flatMap(() => items), [items, repeatCount]);

  useLayoutEffect(() => {
    if (!rootRef.current || !trackRef.current || !items.length) return undefined;
    const mm = gsap.matchMedia();

    mm.add('(prefers-reduced-motion: no-preference)', () => {
      const root = rootRef.current;
      const track = trackRef.current;
      const setX = gsap.quickSetter(track, 'x', 'px');
      let period = 0;
      let x = 0;
      let velocity = 0;
      let hovering = false;
      let dragging = false;
      let lastX = 0;
      let lastT = 0;
      let raf = null;
      let wrap = (v) => v;

      const measure = () => {
        const children = Array.from(track.children);
        const setSize = Math.floor(children.length / repeatCount);
        const first = children.slice(0, setSize);
        if (!first.length) return;
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;
        const progress = period ? (x % -period) / -period : 0;
        period = first.reduce((sum, child) => sum + child.getBoundingClientRect().width, 0) + gap * first.length;
        wrap = gsap.utils.wrap(-period, 0);
        x = wrap(-progress * period);
        setX(x);
      };

      const schedule = () => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(measure);
      };

      const update = () => {
        if (!dragging) {
          if (!(pauseOnHover && hovering)) x -= speed;
          x += velocity;
          velocity *= throwFriction;
          if (Math.abs(velocity) < 0.01) velocity = 0;
        }
        x = wrap(x);
        setX(x);
      };

      measure();

      const drag = Draggable.create(track, {
        type: 'x',
        allowContextMenu: true,
        dragClickables: true,
        onPress() {
          dragging = true;
          velocity = 0;
          this.x = x;
          lastX = this.x;
          lastT = performance.now();
          root.classList.add('is-dragging');
        },
        onDrag() {
          const now = performance.now();
          const dx = this.x - lastX;
          const dt = now - lastT;
          x = wrap(this.x);
          setX(x);
          this.x = x;
          if (dt > 0) velocity = gsap.utils.clamp(-maxThrowVelocity, maxThrowVelocity, (dx / dt) * 36.67 * throwMultiplier);
          lastX = this.x;
          lastT = now;
        },
        onRelease() {
          dragging = false;
          root.classList.remove('is-dragging');
        },
      })[0];

      const enter = () => { hovering = true; };
      const leave = () => { hovering = false; };
      const key = (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        velocity = 0;
        x = wrap(x + (e.key === 'ArrowLeft' ? 1 : -1) * root.clientWidth * 0.35);
        setX(x);
      };

      root.addEventListener('mouseenter', enter);
      root.addEventListener('mouseleave', leave);
      root.addEventListener('keydown', key);
      window.addEventListener('resize', schedule);
      const ro = new ResizeObserver(schedule);
      ro.observe(track);
      track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', schedule);
      });
      gsap.ticker.add(update);

      return () => {
        root.removeEventListener('mouseenter', enter);
        root.removeEventListener('mouseleave', leave);
        root.removeEventListener('keydown', key);
        window.removeEventListener('resize', schedule);
        ro.disconnect();
        if (raf) cancelAnimationFrame(raf);
        gsap.ticker.remove(update);
        drag.kill();
      };
    }, rootRef);

    return () => mm.revert();
  }, [items, speed, repeatCount, pauseOnHover, throwMultiplier, throwFriction, maxThrowVelocity]);

  if (!items.length) return null;

  return (
    <div ref={rootRef} tabIndex={0} role="region" aria-label={label} data-cursor={cursorLabel} className={`ob-marquee ${className}`}>
      <div ref={trackRef} className="ob-marquee__track">
        {duplicated.map((item, index) => (
          <div
            key={`${item.name || 'item'}-${index}`}
            className="ob-marquee__item"
            aria-hidden={index >= items.length}
            data-marquee-copy={index >= items.length ? 'duplicate' : 'original'}
          >
            {renderItem(item, index % items.length)}
          </div>
        ))}
      </div>
    </div>
  );
}
